import React, { useState, useEffect } from 'react';
import { Card, Tabs, Dropdown, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import UserList from './UserList';
import DeactivatedUserList from './DeactivatedUserList';
import DemographicsDashboard from './Demographics/DemographicsDashboard';
import FinanceExportModal from '../Finance/Common/FinanceExportModal';
import roleService, { ROLES } from '../../service/RoleService';

import { userService } from '../../services/userService';
import logger from '../../utils/logger';



const EXPORT_TYPES = {
    'birthday-listing': 'Birthday Listing',
    'work-anniversary': 'Work Anniversary',
    'confirmation-due': 'Confirmation Due',
    'personal-info': 'Personal Info'
};

const EXPORT_FILENAMES = {
    'birthday-listing': 'Birthday_Listing',
    'work-anniversary': 'Work_Anniversary',
    'confirmation-due': 'Confirmation_Due',
    'personal-info': 'Personal_Info_Report'
};

const UserSettings = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab') || 'active';
    const [activeTab, setActiveTab] = useState(tabParam);

    // Export state
    const [selectedExportType, setSelectedExportType] = useState(null);
    const [exportModalVisible, setExportModalVisible] = useState(false);
    const [exporting, setExporting] = useState(false);
    const isManagerScopedOnly = roleService.isManager() && !roleService.hasAnyRole([
        ROLES.ROLE_HR,
        ROLES.ROLE_FINANCE,
        ROLES.ROLE_MANAGEMENT,
        ROLES.ROLE_OWNER
    ]);
    const canExportReports = !isManagerScopedOnly;

    // Sync activeTab with URL parameter
    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab && ['active', 'deactivated', 'demographics'].includes(tab)) {
            if (tab === 'demographics' && isManagerScopedOnly) {
                setActiveTab('active');
                setSearchParams({ tab: 'active' });
                return;
            }
            setActiveTab(tab);
        }
    }, [searchParams, isManagerScopedOnly, setSearchParams]);

    const handleTabChange = (key) => {
        setActiveTab(key);
        setSearchParams({ tab: key });
    };

    // Step 1: User selects which report to export
    const handleExportMenuClick = ({ key }) => {
        setSelectedExportType(key);
        setExportModalVisible(true);
    };

    // Step 2: User chooses local or Google Drive
    const handleExportConfirm = async (exportLocation) => {
        setExportModalVisible(false);
        if (!selectedExportType) return;

        setExporting(true);
        // Pass status param for personal-info export (active/deactivated based on current tab)
        const extraParams = selectedExportType === 'personal-info'
            ? { status: activeTab === 'deactivated' ? 'deactivated' : 'active' }
            : {};
        try {
            if (exportLocation === 'google') {
                const result = await userService.exportReport(selectedExportType, 'google', extraParams);

                if (result?.directLink || result?.webViewLink) {
                    const fileUrl = result.directLink || result.webViewLink;
                    message.success({
                        content: (
                            <span>
                                Export completed! Opening Google Drive...{' '}
                                <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                                    Click here if not opened
                                </a>
                            </span>
                        ),
                        duration: 10,
                    });
                    setTimeout(() => {
                        window.open(fileUrl, '_blank', 'noopener,noreferrer');
                    }, 500);
                } else {
                    message.success('Export uploaded to Google Drive successfully');
                }
            } else {
                // Local download - response is a blob
                const blob = await userService.exportReport(selectedExportType, 'local', extraParams);
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${EXPORT_FILENAMES[selectedExportType]}_${new Date().toISOString().split('T')[0]}.xlsx`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                message.success(`${EXPORT_TYPES[selectedExportType]} exported successfully`);
            }
        } catch (error) {
            logger.error('Export failed:', error);
            message.error('Export failed: ' + (error.message || 'Unknown error'));
        } finally {
            setExporting(false);
            setSelectedExportType(null);
        }
    };

    const handleExportCancel = () => {
        setExportModalVisible(false);
        setSelectedExportType(null);
    };

    const exportMenuItems = Object.entries(EXPORT_TYPES).map(([key, label]) => ({
        key,
        label
    }));

    const tabs = [
        {
            key: 'active',
            label: 'Active Employees',
            children: <UserList />
        },
        {
            key: 'deactivated',
            label: 'Deactivated Employees',
            children: <DeactivatedUserList />
        },
        ...(!isManagerScopedOnly ? [{
            key: 'demographics',
            label: 'Demographics',
            children: <DemographicsDashboard />
        }] : [])
    ];

    return (
        <div className="wrapper-grey finance-container">
            <Card>
                <div className="section-header">
                    <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <h4 style={{ margin: 0, fontSize: 18, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>USER MANAGEMENT</h4>
                        {canExportReports && (
                            <Dropdown
                                menu={{
                                    items: exportMenuItems,
                                    onClick: handleExportMenuClick
                                }}
                                trigger={['click']}
                            >
                                <DownloadOutlined
                                    style={{ fontSize: 16, cursor: 'pointer', color: '#1b1b1b' }}
                                    spin={exporting}
                                />
                            </Dropdown>
                        )}
                    </div>
                </div>

                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    items={tabs}
                />
            </Card>

            {/* Export Target Modal (Local / Google Drive) */}
            <FinanceExportModal
                visible={exportModalVisible}
                onCancel={handleExportCancel}
                onExport={handleExportConfirm}
                title={`Export ${selectedExportType ? EXPORT_TYPES[selectedExportType] : ''}`}
                description="Where do you want the file to be saved?"
            />
        </div>
    );
};

export default UserSettings;
